import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import * as TimezoneCountries from 'countries-and-timezones';
import { createContext, FC, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../constants/queryKeys';
import { Timezone as OriginalTimezone } from 'countries-and-timezones';

dayjs.extend(timezone);

/**
 * Returns the UTC offset in minutes for a given IANA timezone name at the
 * specified date, using the Intl API directly. This is independent of the
 * local system timezone so DST detection works correctly in any environment.
 */
const getUtcOffsetMinutes = (tzName: string, date: Date): number => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tzName,
    timeZoneName: 'longOffset' as Intl.DateTimeFormatOptions['timeZoneName'],
  }).formatToParts(date);
  const offsetStr =
    parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+0';
  // offsetStr is like "GMT+05:30", "GMT-04:00", "GMT+0", or "GMT"
  const match = offsetStr.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] ?? '0', 10);
  return sign * (hours * 60 + minutes);
};

/**
 * Returns true if the given IANA timezone is currently observing Daylight
 * Saving Time. Compares the current offset against the Jan/Jul extremes using
 * the Intl API, so it is accurate regardless of the local system timezone.
 */
const isDST = (tzName: string, date: Date): boolean => {
  const jan = getUtcOffsetMinutes(tzName, new Date(date.getFullYear(), 0, 1));
  const jul = getUtcOffsetMinutes(tzName, new Date(date.getFullYear(), 6, 1));
  if (jan === jul) return false; // no DST observed
  return Math.max(jan, jul) === getUtcOffsetMinutes(tzName, date);
};

interface ContextType {
  timezones: Timezone[];
  userTimezone: Timezone;
  findTimezone: (timezone: string) => Timezone;
}

export interface Timezone {
  id: number;
  value: string;
  name: string;
}

const allTimezones = Object.values(
  TimezoneCountries.getAllTimezones({ deprecated: true })
).filter((timezone) => !timezone.aliasOf);

const formatTimezone = (
  timezone: OriginalTimezone,
  index: number,
  date?: Dayjs
) => {
  let inDST = false;
  try {
    inDST = isDST(timezone.name, date ? date.toDate() : new Date());
  } catch {
    // timezone name not supported by the runtime Intl API; default to standard offset
  }
  return {
    id: index + 1,
    value: `(UTC ${inDST ? timezone.dstOffsetStr : timezone.utcOffsetStr}) ${
      timezone.name
    }`,
    name: timezone.name,
  };
};

const formatAllTimezones = (): Promise<Timezone[]> => {
  const date = dayjs();
  return new Promise((resolve) =>
    resolve(
      allTimezones.map((list, index) => formatTimezone(list, index, date))
    )
  );
};

const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const findTimezone = (timezone: string): Timezone => {
  let timezoneIndex = allTimezones.findIndex((t) => t.name === timezone);
  if (timezoneIndex < 0) {
    // System may return an alias (e.g. 'US/Eastern') — resolve to the canonical name
    const aliasEntry = TimezoneCountries.getTimezone(timezone);
    if (aliasEntry?.aliasOf) {
      timezoneIndex = allTimezones.findIndex(
        (t) => t.name === aliasEntry.aliasOf
      );
    }
  }
  if (timezoneIndex < 0) {
    // 'UTC' is itself an alias of 'Etc/UTC' in countries-and-timezones, so
    // search for the canonical entry 'Etc/UTC' rather than the alias.
    timezoneIndex = allTimezones.findIndex((t) => t.name === 'Etc/UTC');
  }
  return timezoneIndex >= 0
    ? formatTimezone(allTimezones[timezoneIndex], timezoneIndex)
    : formatTimezone(allTimezones[0], 0);
};

const useTimezonesProvider = (): ContextType => {
  const initialData = findTimezone(getUserTimeZone());

  const { data = [initialData] } = useQuery(
    [QUERY_KEYS.TIMEZONES.TIMEZONES_LIST],
    () => formatAllTimezones(),
    {
      refetchOnWindowFocus: false,
    }
  );

  return {
    timezones: data,
    userTimezone: initialData,
    findTimezone,
  };
};

const TimezonesContext = createContext<ContextType>({} as ContextType);

export const TimezonesProvider: FC = ({ children }) => {
  const timezones = useTimezonesProvider();

  return (
    <TimezonesContext.Provider value={timezones}>
      {children}
    </TimezonesContext.Provider>
  );
};

export const useTimezones = () => useContext(TimezonesContext);
